import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    Box, Stack, Typography, Button, CircularProgress, Snackbar, TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import TableChartIcon from '@mui/icons-material/TableChart';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SendIcon from '@mui/icons-material/Send';
import EditIcon from '@mui/icons-material/Edit';
import HistoryIcon from '@mui/icons-material/History';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TablePagination from '@mui/material/TablePagination';
import {
    fetchColumnDefs,
    fetchGlSheetPage,
    fetchGlSheetAllRows,
    GlColumnDef, GlSheetRowVO,
} from '@/services/accountService/glInvoiceService';
import { changeFs, changeStatus } from '@/services/auditService/auditFsAnnualService';
import { AnnualFsStatusEnum } from '@/services/auditService';
import { useFsCategoryStore } from '@/store/audit';
import { buildFsLinesFromGl } from '@/utils/glToFsMapper';
import {
    findUnbalancedRows,
    validateGlSubmitHard,
    type UnbalancedRowInfo,
} from '@/utils/glSubmitValidation';
import type { FsLine } from '@/utils/glToFsMapper';
import { buildExcludedBankSummaryKeys } from '@/components/account/gl-invoice/mode/tabs/bankColumnUtils';
import GlSheetTable from '@/pages/components/account/gl-invoice/mode/GlSheetTable';
import GlSheetEditView from '@/pages/components/account/gl-invoice/mode/GlSheetEditView';
import GlTinyColumn from '@/pages/components/account/gl-invoice/mode/GlTinyColumn';
import FsDrawer from '@/pages/components/account/gl-invoice/mode/FsDrawer';
import GlSubmitUnbalancedDialog from '@/pages/components/account/gl-invoice/mode/GlSubmitUnbalancedDialog';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import BadgeIcon from '@mui/icons-material/Badge';
import GlImportHistoryDialog from '@/pages/components/account/gl-invoice/GlImportHistoryDialog';
import GlTransferHistoryDialog from '@/pages/components/account/gl-invoice/GlTransferHistoryDialog';
import BankStatementImportDialog from '@/pages/components/account/gl-invoice/bank-statement/BankStatementImportDialog';
import { tinyRowMatchesQuery } from '@/pages/components/account/gl-invoice/mode/matchTinyRow';
import { useShowClientAlias } from '@/pages/components/account/gl-invoice/useShowClientAlias';

interface GlDetailProps {
    companyId: number;
    annualId: number;
    companyName: string;
    yearOfAssessment: string;
    /** 审计区间起止（DD/MM/YYYY），约束 Edit Date */
    periodFrom?: string | null;
    periodTo?: string | null;
    onBack: () => void;
    onSubmitSuccess?: (message?: string) => void;
}

export default function GlDetail(props: GlDetailProps) {
    const {
        companyId, annualId, companyName, yearOfAssessment,
        periodFrom, periodTo, onBack, onSubmitSuccess,
    } = props;
    const { showClientAlias, setShowClientAlias } = useShowClientAlias();

    const [columnDefs, setColumnDefs] = useState<GlColumnDef[]>([]);
    const [rows, setRows] = useState<GlSheetRowVO[]>([]);
    const [totalRow, setTotalRow] = useState(0);
    const [summaryR6, setSummaryR6] = useState<Record<string, number>>({});
    const [summaryR10, setSummaryR10] = useState<Record<string, number>>({});
    const [summaryR10Remarks, setSummaryR10Remarks] = useState<Record<string, string>>({});
    const [pageNum, setPageNum] = useState(1);
    const [pageSize, setPageSize] = useState(50);
    const [loading, setLoading] = useState(false);
    const [sheetQuery, setSheetQuery] = useState('');
    const [allRows, setAllRows] = useState<GlSheetRowVO[]>([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchPage, setSearchPage] = useState(0);

    // 視圖切換：'original' = GlSheetTable, 'tiny' = GlTinyColumn
    const [viewMode, setViewMode] = useState<'original' | 'tiny'>('original');
    /** Tiny Mode 需全量行做科目筛选；与 Original 分页 rows 分离 */
    const [tinyRows, setTinyRows] = useState<GlSheetRowVO[]>([]);
    const [tinyLoading, setTinyLoading] = useState(false);

    // FS Summary Drawer state
    const [fsSummaryOpen, setFsSummaryOpen] = useState(false);
    /** FS Summary 点击科目后，进入 Tiny 并定位到对应 tab */
    const [tinyFocusFsCode, setTinyFocusFsCode] = useState<string | null>(null);
    const [importHistoryOpen, setImportHistoryOpen] = useState(false);
    const [transferHistoryOpen, setTransferHistoryOpen] = useState(false);
    const [bankImportOpen, setBankImportOpen] = useState(false);

    // Submit state
    const [submitting, setSubmitting] = useState(false);
    const [snack, setSnack] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
        { open: false, message: '', severity: 'success' }
    );

    // Edit mode
    const [editMode, setEditMode] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [editRows, setEditRows] = useState<GlSheetRowVO[]>([]);

    // Submit 不平衡行确认弹窗
    const [unbalancedDialogOpen, setUnbalancedDialogOpen] = useState(false);
    const [unbalancedRows, setUnbalancedRows] = useState<UnbalancedRowInfo[]>([]);
    const [unbalancedConfirming, setUnbalancedConfirming] = useState(false);
    const pendingFsLinesRef = useRef<{ bsLines: FsLine[]; plLines: FsLine[] } | null>(null);

    // 预加载 fsCategoryStore（优化项 5.2）
    useEffect(() => {
        useFsCategoryStore.getState().initIfNeeded(companyId, annualId).catch(() => {});
    }, [companyId, annualId]);

    // 离开 GL 页必须清空：否则带回 Audit 会污染 BS-EQ-AL / Last Year 等展示
    useEffect(() => {
        return () => {
            useFsCategoryStore.getState().reset();
        };
    }, []);

    const executeFsSubmit = useCallback(async (bsLines: FsLine[], plLines: FsLine[]) => {
        await changeFs({
            companyId,
            annualId,
            balanceSheetLines: bsLines,
            profitLossLines: plLines,
            syncWpFromGl: true,
        });
        await changeStatus(companyId, annualId, AnnualFsStatusEnum.AUDIT_ADJUSTMENT);
        // GL 页预热 / FS Summary 注入的 store 不能带到 Audit：否则 BS-EQ-AL 会优先用过期 bsValues(常为 0)
        useFsCategoryStore.getState().reset();
        setSnack({ open: true, message: 'Submitted successfully', severity: 'success' });
        onSubmitSuccess?.('Submitted successfully');
    }, [companyId, annualId, onSubmitSuccess]);

    const handleUnbalancedDialogCancel = useCallback(() => {
        setUnbalancedDialogOpen(false);
        setUnbalancedRows([]);
        pendingFsLinesRef.current = null;
    }, []);

    const handleUnbalancedDialogConfirm = useCallback(async () => {
        const pending = pendingFsLinesRef.current;
        if (!pending) return;
        setUnbalancedConfirming(true);
        try {
            await executeFsSubmit(pending.bsLines, pending.plLines);
            handleUnbalancedDialogCancel();
        } catch (e: any) {
            console.error('GL Submit failed:', e);
            setSnack({ open: true, message: e?.message || 'Submit failed', severity: 'error' });
        } finally {
            setUnbalancedConfirming(false);
        }
    }, [executeFsSubmit, handleUnbalancedDialogCancel]);

    // GL Submit：校验 → 映射（与 FS Summary 同源）→ changeFs → changeStatus
    const handleGlSubmit = async () => {
        if (submitting) return;
        try {
            setSubmitting(true);

            const store = useFsCategoryStore.getState();
            await store.initIfNeeded(companyId, annualId);

            const { bsLines, plLines, missingFsCodes } = buildFsLinesFromGl({
                columnDefs,
                summaryCurrentPeriod: summaryR6,
                summaryPriorYear: summaryR10,
                byCode: store.byCode,
                bsTree: store.bsTree,
                plTree: store.plTree,
                yearOfAssessment,
            });

            const hardErrors = validateGlSubmitHard({ totalRow, missingFsCodes });
            if (hardErrors.length > 0) {
                setSnack({ open: true, message: hardErrors.join(' '), severity: 'error' });
                return;
            }

            const sheetRes = await fetchGlSheetAllRows(companyId, annualId, totalRow);
            // 外币原币 / Ex-rate 仅作参考，不参与借贷平衡（与 FS / 银行汇总口径一致）
            const excludeKeys = buildExcludedBankSummaryKeys(columnDefs);
            const unbalanced = findUnbalancedRows(sheetRes.list || [], excludeKeys);
            if (unbalanced.length > 0) {
                pendingFsLinesRef.current = { bsLines, plLines };
                setUnbalancedRows(unbalanced);
                setUnbalancedDialogOpen(true);
                return;
            }

            await executeFsSubmit(bsLines, plLines);

        } catch (e: any) {
            console.error('GL Submit failed:', e);
            setSnack({ open: true, message: e?.message || 'Submit failed', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    // Load data
    const loadData = useCallback(async (page?: number, size?: number) => {
        const p = page ?? pageNum;
        const s = size ?? pageSize;
        setLoading(true);
        try {
            const res = await fetchGlSheetPage({
                companyId, annualId, pageNumber: p, pageSize: s,
            });
            setRows(res.list || []);
            setTotalRow(res.totalRow || 0);
            setSummaryR6(res.summaryCurrentPeriod || {});
            setSummaryR10(res.summaryPriorYear || {});
            setSummaryR10Remarks(res.summaryPriorYearRemarks || {});
            setTinyRows([]);
            setAllRows([]);
            // 列表只展示 sheet 返回的有数据列；无发票时保持空列，标准 defs 仅在进 Edit 时再拉
            setColumnDefs(res.columnDefs?.length ? res.columnDefs : []);
        } catch (err) {
            console.error('Failed to load GL sheet data:', err);
        } finally {
            setLoading(false);
        }
    }, [companyId, annualId, pageNum, pageSize]);

    const sheetQueryTrimmed = sheetQuery.trim();
    const searching = sheetQueryTrimmed !== '';

    useEffect(() => {
        setSearchPage(0);
    }, [sheetQueryTrimmed]);

    useEffect(() => {
        if (!searching || viewMode !== 'original' || totalRow <= 0) return;
        if (allRows.length >= totalRow) return;
        if (tinyRows.length >= totalRow) {
            setAllRows(tinyRows);
            return;
        }
        if (rows.length >= totalRow) {
            setAllRows(rows);
            return;
        }
        let cancelled = false;
        setSearchLoading(true);
        fetchGlSheetAllRows(companyId, annualId, totalRow)
            .then((res) => {
                if (cancelled) return;
                setAllRows(res.list || []);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error('Failed to load rows for search:', err);
                setSnack({ open: true, message: 'Failed to search invoices', severity: 'error' });
            })
            .finally(() => {
                if (!cancelled) setSearchLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [searching, viewMode, totalRow, allRows.length, tinyRows, rows, companyId, annualId]);

    const filteredRows = useMemo(() => {
        if (!searching) return rows;
        return allRows.filter((row) => tinyRowMatchesQuery(row, sheetQueryTrimmed));
    }, [searching, rows, allRows, sheetQueryTrimmed]);

    const displayRows = searching
        ? filteredRows.slice(searchPage * pageSize, searchPage * pageSize + pageSize)
        : rows;
    const displayCount = searching ? filteredRows.length : totalRow;
    const displayPage = searching
        ? Math.max(0, Math.min(searchPage, Math.max(0, Math.ceil(filteredRows.length / pageSize) - 1)))
        : Math.max(0, Math.min(Math.ceil(totalRow / pageSize) - 1, pageNum - 1));

    const enterTinyMode = useCallback(async () => {
        setViewMode('tiny');
        if (totalRow <= 0) return;
        if (tinyRows.length >= totalRow) return;
        if (rows.length >= totalRow) {
            setTinyRows(rows);
            return;
        }

        setTinyLoading(true);
        try {
            const res = await fetchGlSheetAllRows(companyId, annualId, totalRow);
            setTinyRows(res.list || []);
            if (res.summaryCurrentPeriod) setSummaryR6(res.summaryCurrentPeriod);
            if (res.summaryPriorYear) setSummaryR10(res.summaryPriorYear);
            if (res.summaryPriorYearRemarks) setSummaryR10Remarks(res.summaryPriorYearRemarks);
            if (res.columnDefs?.length) setColumnDefs(res.columnDefs);
        } catch (err) {
            console.error('Failed to load rows for tiny mode:', err);
            setSnack({ open: true, message: 'Failed to load data for Tiny Mode', severity: 'error' });
            setViewMode('original');
            setTinyFocusFsCode(null);
        } finally {
            setTinyLoading(false);
        }
    }, [companyId, annualId, totalRow, tinyRows.length, rows]);

    const handleNavigateToTinyFromFs = useCallback((fsCode: string) => {
        setFsSummaryOpen(false);
        setTinyFocusFsCode(fsCode);
        void enterTinyMode();
    }, [enterTinyMode]);

    /** Tiny Mode 转移成功后静默重拉（不设 tinyLoading，避免卸载抽屉） */
    const refreshTinyAfterTransfer = useCallback(async () => {
        try {
            const [allRes, pageRes] = await Promise.all([
                fetchGlSheetAllRows(companyId, annualId, Math.max(totalRow, 1)),
                fetchGlSheetPage({
                    companyId,
                    annualId,
                    pageNumber: pageNum,
                    pageSize,
                }),
            ]);
            setTinyRows(allRes.list || []);
            setTotalRow(allRes.totalRow || pageRes.totalRow || 0);
            if (allRes.summaryCurrentPeriod) setSummaryR6(allRes.summaryCurrentPeriod);
            else if (pageRes.summaryCurrentPeriod) setSummaryR6(pageRes.summaryCurrentPeriod);
            if (allRes.summaryPriorYear) setSummaryR10(allRes.summaryPriorYear);
            else if (pageRes.summaryPriorYear) setSummaryR10(pageRes.summaryPriorYear);
            if (allRes.summaryPriorYearRemarks) setSummaryR10Remarks(allRes.summaryPriorYearRemarks);
            else if (pageRes.summaryPriorYearRemarks) setSummaryR10Remarks(pageRes.summaryPriorYearRemarks);
            if (allRes.columnDefs?.length) setColumnDefs(allRes.columnDefs);
            else if (pageRes.columnDefs?.length) setColumnDefs(pageRes.columnDefs);
            setRows(pageRes.list || []);
            setSnack({ open: true, message: 'Invoice transferred', severity: 'success' });
        } catch (err) {
            console.error('Failed to refresh after transfer:', err);
            setSnack({ open: true, message: 'Transferred, but failed to refresh list', severity: 'error' });
        }
    }, [companyId, annualId, totalRow, pageNum, pageSize]);

    useEffect(() => { loadData(); }, [loadData]);

    const enterEditMode = useCallback(async () => {
        setEditLoading(true);
        try {
            const res = await fetchGlSheetAllRows(companyId, annualId, totalRow);
            setEditRows(res.list || []);
            if (res.columnDefs?.length) {
                setColumnDefs(res.columnDefs);
            } else if (columnDefs.length === 0) {
                const defs = await fetchColumnDefs();
                if (defs?.length) setColumnDefs(defs);
            }
            setEditMode(true);
            setViewMode('original');
        } catch (err) {
            console.error('Failed to load rows for edit:', err);
            setSnack({ open: true, message: 'Failed to load data for editing', severity: 'error' });
        } finally {
            setEditLoading(false);
        }
    }, [companyId, annualId, totalRow, columnDefs.length]);

    const handleEditSaved = useCallback(() => {
        setEditMode(false);
        setEditRows([]);
        setSnack({ open: true, message: 'Saved successfully', severity: 'success' });
        loadData(1, pageSize);
    }, [loadData, pageSize]);

    const handleEditCancel = useCallback(() => {
        setEditMode(false);
        setEditRows([]);
    }, []);

    return (
        <Box>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={2} mb={2}>
                <Button
                    variant="text"
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{
                        textTransform: 'none',
                        fontWeight: 600,
                        fontSize: 14,
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main', bgcolor: 'action.hover' },
                    }}
                >
                    Back
                </Button>
                <Typography variant="h6" fontWeight={700}>
                    GL Invoice — {companyName} ({yearOfAssessment})
                </Typography>
                <Box sx={{ flex: 1 }} />
                {!editMode && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SwapHorizIcon />}
                        onClick={() => setTransferHistoryOpen(true)}
                        disabled={loading}
                        sx={{ textTransform: 'none' }}
                    >
                        Transfer History
                    </Button>
                )}
                {!editMode && (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={editLoading ? <CircularProgress size={14} /> : <EditIcon />}
                        onClick={enterEditMode}
                        disabled={editLoading || loading}
                        sx={{ textTransform: 'none' }}
                    >
                        Edit
                    </Button>
                )}
                {/* Tiny / Original 切換按鈕 */}
                {!editMode && (viewMode === 'original' ? (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ViewColumnIcon />}
                        onClick={enterTinyMode}
                        disabled={tinyLoading || loading || totalRow === 0}
                        sx={{ textTransform: 'none' }}
                    >
                        Tiny Mode
                    </Button>
                ) : (
                    <Button
                        variant="outlined"
                        size="small"
                        startIcon={<TableChartIcon />}
                        onClick={() => setViewMode('original')}
                        sx={{ textTransform: 'none' }}
                    >
                        Original Mode
                    </Button>
                ))}
                {!editMode && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PictureAsPdfIcon />}
                    onClick={() => setBankImportOpen(true)}
                    disabled={loading}
                    sx={{ textTransform: 'none' }}
                >
                    Import Bank PDF
                </Button>
                )}
                {!editMode && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={() => setImportHistoryOpen(true)}
                    sx={{ textTransform: 'none' }}
                >
                    Import History
                </Button>
                )}
                {!editMode && (
                <Button
                    variant="outlined"
                    size="small"
                    startIcon={<AccountBalanceIcon />}
                    onClick={() => setFsSummaryOpen(true)}
                    sx={{ textTransform: 'none' }}
                >
                    FS Summary
                </Button>
                )}
                {!editMode && (
                <Button
                    variant={showClientAlias ? 'contained' : 'outlined'}
                    size="small"
                    color={showClientAlias ? 'info' : 'inherit'}
                    startIcon={<BadgeIcon />}
                    onClick={() => setShowClientAlias(!showClientAlias)}
                    disabled={loading || columnDefs.length === 0}
                    sx={{ textTransform: 'none' }}
                    title={showClientAlias ? 'Hide client names on columns' : 'Show client names on columns'}
                >
                    {showClientAlias ? 'Client names on' : 'Client names'}
                </Button>
                )}
                {!editMode && (
                <Button
                    variant="contained"
                    size="small"
                    color="primary"
                    startIcon={submitting ? <CircularProgress size={14} color="inherit" /> : <SendIcon />}
                    onClick={handleGlSubmit}
                    disabled={submitting || columnDefs.length === 0}
                    sx={{ textTransform: 'none' }}
                >
                    Submit
                </Button>
                )}
            </Stack>

            {/* Loading overlay */}
            {loading && !editMode && (
                <Box sx={{ textAlign: 'center', py: 2, color: 'text.secondary', fontSize: 13 }}>
                    Loading...
                </Box>
            )}

            {/* 視圖區域 */}
            {editMode ? (
                <GlSheetEditView
                    companyId={companyId}
                    annualId={annualId}
                    columnDefs={columnDefs}
                    initialRows={editRows}
                    summaryPriorYear={summaryR10}
                    summaryPriorYearRemarks={summaryR10Remarks}
                    periodFrom={periodFrom}
                    periodTo={periodTo}
                    onSaved={handleEditSaved}
                    onCancel={handleEditCancel}
                />
            ) : viewMode === 'original' ? (
                <>
                    <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                        <TextField
                            size="small"
                            value={sheetQuery}
                            placeholder="Invoice no., date, description, amount"
                            onChange={(e) => setSheetQuery(e.target.value)}
                            sx={{ width: 320 }}
                        />
                        {searching && (
                            <Typography variant="caption" color="text.secondary">
                                {searchLoading ? 'Searching...' : `${filteredRows.length} ${filteredRows.length === 1 ? 'match' : 'matches'}`}
                            </Typography>
                        )}
                    </Stack>
                    <GlSheetTable
                        columnDefs={columnDefs}
                        rows={displayRows}
                        summaryCurrentPeriod={summaryR6}
                        summaryPriorYear={summaryR10}
                        showClientAlias={showClientAlias}
                    />

                    {/* Pagination */}
                    <TablePagination
                        component="div"
                        count={displayCount}
                        page={displayPage}
                        onPageChange={(_, p) => {
                            if (searching) setSearchPage(p);
                            else setPageNum(p + 1);
                        }}
                        rowsPerPage={pageSize}
                        onRowsPerPageChange={e => {
                            setPageSize(parseInt(e.target.value, 10) || 50);
                            setPageNum(1);
                        }}
                        rowsPerPageOptions={[20, 50, 100, 200]}
                        sx={{ mt: 1 }}
                    />
                </>
            ) : (
                tinyLoading ? (
                    <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary', fontSize: 13 }}>
                        Loading all rows for Tiny Mode...
                    </Box>
                ) : (
                <GlTinyColumn
                    companyId={companyId}
                    annualId={annualId}
                    columnDefs={columnDefs}
                    rows={tinyRows.length > 0 ? tinyRows : rows}
                    summaryCurrentPeriod={summaryR6}
                    summaryPriorYear={summaryR10}
                    onTransferSuccess={refreshTinyAfterTransfer}
                    focusFsCode={tinyFocusFsCode}
                    onFocusFsCodeConsumed={() => setTinyFocusFsCode(null)}
                    showClientAlias={showClientAlias}
                />
                )
            )}

            {/* FS Summary Drawer */}
            <FsDrawer
                open={fsSummaryOpen}
                onClose={() => setFsSummaryOpen(false)}
                companyId={companyId}
                annualId={annualId}
                columnDefs={columnDefs}
                summaryCurrentPeriod={summaryR6}
                summaryPriorYear={summaryR10}
                onNavigateToTiny={handleNavigateToTinyFromFs}
                showClientAlias={showClientAlias}
                onShowClientAliasChange={setShowClientAlias}
            />

            <GlImportHistoryDialog
                open={importHistoryOpen}
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
                columnDefs={columnDefs}
                onClose={() => setImportHistoryOpen(false)}
            />

            <GlTransferHistoryDialog
                open={transferHistoryOpen}
                companyId={companyId}
                annualId={annualId}
                companyName={companyName}
                columnDefs={columnDefs}
                onClose={() => setTransferHistoryOpen(false)}
                onReverted={refreshTinyAfterTransfer}
            />

            <BankStatementImportDialog
                open={bankImportOpen}
                companyId={companyId}
                annualId={annualId}
                onClose={() => setBankImportOpen(false)}
                onSaved={(message) => {
                    setBankImportOpen(false);
                    setSnack({ open: true, message, severity: 'success' });
                    void loadData(1, pageSize);
                }}
            />

            {/* Snackbar for submit feedback */}
            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack(s => ({ ...s, open: false }))}
                message={snack.message}
            />

            <GlSubmitUnbalancedDialog
                open={unbalancedDialogOpen}
                rows={unbalancedRows}
                confirming={unbalancedConfirming}
                onCancel={handleUnbalancedDialogCancel}
                onConfirm={handleUnbalancedDialogConfirm}
            />
        </Box>
    );
}
